import { SellersToBookResponse } from '@hermes/types'
import { BookIcon, Box, Button, InfoCircle, MapIcon, TruckIcon, Typography, useTheme } from '@hermes/web-components'

import Avatar from '@components/Avatar'
import BookImage from '@components/BookImage'
import useRecommendedSellerInfoModal from '@hooks/useRecommendedSellerInfo'
import useTranslateMessage from '@hooks/useTranslateMessage'
import { conditionsDataToPoEditorMapper } from '@lib/utils/languageHelpers'

import { isListingDto } from '@containers/Checkout/CheckoutPage'
import SellerListingSkeleton from './SellerListingCardSkeleton'

import {
  BookImageContainer,
  RecommendedSellerTitle,
  RecommendedSellerTitleContainer,
  RightChevron,
  SeeAllButton,
  SellerFullName,
  SellerPrice
} from './SellerListingCard.styles'

interface SellerListingProps {
  data: SellersToBookResponse['data'][0] | null
  isLoading?: boolean
  loggedInUserIsSeller: boolean
  onClick: (data: SellersToBookResponse['data'][0]) => void
  viewOptions: Partial<{
    isHighlighted?: boolean
    isRecommended?: boolean
    showDeliveryMethod?: boolean
    showPriceInCta?: boolean
    showSellerLocation?: boolean
    showInfoButton?: boolean
    showTotalPrice?: boolean
  }>
}

export const SellerListingCard = ({
  data,
  isLoading = false,
  loggedInUserIsSeller,
  onClick,
  viewOptions: {
    isHighlighted = false,
    isRecommended = false,
    showDeliveryMethod = false,
    showPriceInCta = false,
    showSellerLocation = false,
    showInfoButton = false
  }
}: SellerListingProps) => {
  const theme = useTheme()
  const { t } = useTranslateMessage()
  const { RecommendedSellerInfoModal, toggleModalOpen } = useRecommendedSellerInfoModal()

  const localIsHighlighted = isRecommended || isHighlighted

  const onSelected = () => {
    if (!data || loggedInUserIsSeller) {
      return null
    }

    onClick(data)
  }

  const onInfoClicked = () => {
    toggleModalOpen()
  }

  if (isLoading) {
    return <SellerListingSkeleton isHighlighted={localIsHighlighted} />
  }

  if (!data) {
    return <></>
  }

  const hasOtherListings = data?.seller?.listings && data?.seller?.listings.length > 0

  const priceString = t(
    { id: 'price' },
    {
      price: isListingDto(data) ? data?.totalPrice : data.price
    }
  )
  const ctaButtonString = showPriceInCta ? `${t({ id: 'select' })} ∙ ${priceString}` : t({ id: 'selectSeller' })

  const uniqueListings =
    data?.seller?.listings?.reduce(
      (acc: typeof data.seller.listings, listing) => {
        if (!acc.find((l) => l.book.id === listing.book.id)) {
          acc.push(listing)
        }
        return acc
      },
      [] as typeof data.seller.listings
    ) ?? []

  return (
    <Box
      style={{
        borderRadius: '12px',
        backgroundColor: localIsHighlighted ? theme.palette.neutral[100] : 'transparent'
      }}
    >
      {localIsHighlighted && (
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <RecommendedSellerTitleContainer isRecommended={isRecommended}>
            <RecommendedSellerTitle fontSize="10px" lineHeight="12px" color={theme.palette.neutral.main}>
              {isRecommended ? t({ id: 'recommendedSeller' }) : t({ id: 'highestRankedSeller' })}
            </RecommendedSellerTitle>
            {showInfoButton && (
              <Box ml={'6px'} style={{ display: 'flex' }}>
                <InfoCircle
                  onClick={onInfoClicked}
                  width="12px"
                  height="12px"
                  fill={theme.palette.neutral.main}
                  style={{ cursor: 'pointer' }}
                />
              </Box>
            )}
          </RecommendedSellerTitleContainer>

          {showDeliveryMethod && (
            <Box display="flex" columnGap="5px" marginRight={'12px'}>
              <TruckIcon width="12px" height="12px" fill={theme.palette.neutral.main} />
              <Typography fontSize="10px" lineHeight="12px" color={theme.palette.neutral.main}>
                {t({ id: 'homeToHomeDelivery' })}
              </Typography>
            </Box>
          )}
        </Box>
      )}

      <Box style={{ padding: localIsHighlighted ? '16px 12px 12px 12px' : '' }}>
        <Box display="flex" justifyContent="space-between" m="0">
          <Box width="40px" height="40px" position="relative">
            <Avatar
              src={data?.seller.imageUrl}
              width={40}
              height={40}
              alt={`Seller ${data?.seller.firstName} profile image.`}
            />
          </Box>

          <Box display="flex" flexDirection="column" ml="8px" flex="1" overflow="hidden">
            <SellerFullName variant="body2" fontWeight={500}>
              {data?.seller.firstName}
            </SellerFullName>
            <Box mb="5px">
              {data?.condition && (
                <Typography fontSize="12px">
                  <BookIcon width="10px" style={{ marginRight: '5px' }} />
                  {t({ id: conditionsDataToPoEditorMapper(data?.condition) })}
                </Typography>
              )}
              {showSellerLocation && (
                <Typography fontSize="12px">
                  <MapIcon width="10px" style={{ marginRight: '5px' }} />
                  {data?.seller.address.city}
                </Typography>
              )}
            </Box>
          </Box>

          <Box display="flex" flexDirection="column" ml="8px">
            {showPriceInCta ? (
              <Button
                onClick={onSelected}
                variant="contained"
                disabled={loggedInUserIsSeller}
                sx={{
                  height: 'auto',
                  width: 'auto',
                  fontSize: '14px',
                  lineHeight: '20px',
                  whiteSpace: 'nowrap'
                }}
              >
                {ctaButtonString}
              </Button>
            ) : (
              <Box display="flex" flexDirection="column">
                <SellerPrice variant="body2" fontWeight={500} align="right">
                  {priceString}
                </SellerPrice>
                <Typography fontSize="12px" color={theme.palette.neutral.main} align="right">
                  {t({ id: 'shippingAndHandling' })}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
        {!showPriceInCta && (
          <Box marginTop="12px" marginBottom="12px">
            <Button
              onClick={onSelected}
              variant="contained"
              disabled={loggedInUserIsSeller}
              sx={{
                height: 'auto',
                width: '100%',
                lineHeight: '20px',
                whiteSpace: 'nowrap'
              }}
            >
              {ctaButtonString}
            </Button>
          </Box>
        )}

        {hasOtherListings && (
          <Box ml="50px">
            <Typography fontSize="12px" lineHeight="16px" mb="8px">
              {`${data?.seller.firstName} `}{' '}
              {t({ id: 'saleWithCollection.isAlsoSelling', defaultMessage: 'is also selling' })}
            </Typography>
            <Box display="flex" height="99px" overflow="hidden" flexWrap="wrap" gap="8px">
              {uniqueListings.map((listing) => (
                <BookImageContainer key={listing?.uuid} onClick={onSelected} disabled={loggedInUserIsSeller}>
                  <BookImage width={60} height={99} src={listing?.book.smallImage} alt={listing?.book.title ?? ''} />
                </BookImageContainer>
              ))}
            </Box>
            <SeeAllButton variant="ghost" onClick={onSelected} disabled={loggedInUserIsSeller}>
              {t({ id: 'common.see_all', defaultMessage: 'See all' })}
              <RightChevron width="20px" />
            </SeeAllButton>
          </Box>
        )}
      </Box>

      <RecommendedSellerInfoModal />
    </Box>
  )
}
